import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGoogleReCaptcha } from "@google-recaptcha/react";
import PlatformLayout from "../../components/platform/PlatformLayout";
import { PlatformZone } from "../../components/platform/PlatformZone";
import { FaCheckCircle } from "react-icons/fa";
import { api } from "../../services/api";
import { PlanSelector } from "../../components/ui/PlanSelector";
import { BusinessModelSelector } from "../../components/ui/BusinessModelSelector";
import { BusinessTypeSelector } from "../../components/ui/BusinessTypeSelector";
import { usePricingPlans } from "../../hooks/usePricingPlans";

const Onboarding: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const googleReCaptcha = useGoogleReCaptcha();
  const preselectedPlan = searchParams.get("plan");
  const { plans, loading: plansLoading } = usePricingPlans();
  const [formData, setFormData] = useState({
    name: "",
    subdomain: "",
    adminEmail: "",
    adminName: "",
    plan: preselectedPlan || "",
    businessModel: "pay-as-you-go" as "plugin-based" | "pay-as-you-go",
    businessType: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Get platform domain for preview
  const getPlatformDomain = (): string => {
    if (typeof window !== "undefined") {
      const hostname = window.location.hostname;
      if (hostname.includes("localhost") || hostname.includes("local")) {
        return "localhost";
      }
      // Extract base domain (remove subdomain if present)
      const parts = hostname.split(".");
      if (parts.length > 2) {
        return parts.slice(-2).join(".");
      }
      return hostname;
    }
    return (
      process.env.REACT_APP_PLATFORM_DOMAIN?.replace(/^https?:\/\//, "") ||
      "localhost"
    );
  };

  // Update plan when preselected plan changes or plans load
  useEffect(() => {
    if (preselectedPlan && !formData.plan) {
      setFormData((prev) => ({ ...prev, plan: preselectedPlan }));
    }
  }, [preselectedPlan]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateSubdomain = (subdomain: string): boolean => {
    // Subdomain validation: 3-63 chars, alphanumeric and hyphens, can't start/end with hyphen
    const pattern = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/i;
    return (
      subdomain.length >= 3 && subdomain.length <= 63 && pattern.test(subdomain)
    );
  };

  const handleSubdomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
    // Remove consecutive hyphens
    value = value.replace(/-+/g, "-");
    // Remove leading/trailing hyphens
    value = value.replace(/^-+|-+$/g, "");

    setFormData((prev) => ({
      ...prev,
      subdomain: value,
    }));

    if (errors.subdomain) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.subdomain;
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name =
        t("platform.onboarding.errors.nameRequired") ||
        "Store name is required";
    }

    if (!formData.subdomain.trim()) {
      newErrors.subdomain =
        t("platform.onboarding.errors.subdomainRequired") ||
        "Subdomain is required";
    } else if (!validateSubdomain(formData.subdomain)) {
      newErrors.subdomain =
        t("platform.onboarding.errors.subdomainInvalid") ||
        "Subdomain must be 3-63 characters, alphanumeric with hyphens";
    }

    if (!formData.adminEmail.trim()) {
      newErrors.adminEmail =
        t("platform.onboarding.errors.emailRequired") ||
        "Admin email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
      newErrors.adminEmail =
        t("platform.onboarding.errors.emailInvalid") ||
        "Please enter a valid email address";
    }

    if (!formData.businessType.trim()) {
      newErrors.businessType =
        t("platform.onboarding.errors.businessTypeRequired") ||
        "Business type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // In test mode (Cypress), bypass reCAPTCHA
      const isTestMode =
        typeof window !== "undefined" && (window as any).Cypress;
      let captchaValue = "";

      if (isTestMode) {
        captchaValue = "test-token-cypress";
      } else {
        // Check if reCAPTCHA is available
        if (!googleReCaptcha) {
          setErrors({
            form:
              t("platform.onboarding.errors.captchaNotReady") ||
              "CAPTCHA is not ready. Please refresh the page and try again.",
          });
          setLoading(false);
          return;
        }

        try {
          captchaValue = await (googleReCaptcha as any)?.executeV3(
            "onboarding"
          );
          if (!captchaValue || captchaValue.trim() === "") {
            setErrors({
              form:
                t("platform.onboarding.errors.captchaNotReady") ||
                "CAPTCHA verification failed. Please refresh the page and try again.",
            });
            setLoading(false);
            return;
          }
        } catch (captchaError: any) {
          console.error("reCAPTCHA execution error:", captchaError);
          setErrors({
            form:
              t("platform.onboarding.errors.captchaNotReady") ||
              "CAPTCHA verification failed. Please refresh the page and try again.",
          });
          setLoading(false);
          return;
        }
      }

      // Map plan value to plan name for submission
      let planName: string | undefined = undefined;
      if (formData.plan) {
        const selectedPlan = plans.find((p) => p.value === formData.plan);
        if (selectedPlan) {
          planName = selectedPlan.label;
        }
      }

      // Submit form
      const response = await api.post("/public/stores", {
        name: formData.name.trim(),
        subdomain: formData.subdomain.trim(),
        adminEmail: formData.adminEmail.trim(),
        adminName: formData.adminName.trim() || undefined,
        plan: planName, // Send plan name instead of value
        businessModel: formData.businessModel,
        businessType: formData.businessType.trim(),
        captchaValue,
      });

      if (response.status === 201) {
        setSuccess(true);
        // Redirect after 3 seconds
        setTimeout(() => {
          navigate("/");
        }, 10000);
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.errors?.[0]?.msg ||
        t("platform.onboarding.errors.submitFailed") ||
        "Failed to submit store request. Please try again.";
      setErrors({ form: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <PlatformLayout showHeader={true} headerSticky={false}>
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-blue-50 px-4">
          <div className="max-w-2xl w-full bg-white p-8 md:p-12 text-center">
            <div className="mb-6">
              <FaCheckCircle className="text-green-500 text-6xl mx-auto" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t("platform.onboarding.success.title") ||
                "Store Request Submitted!"}
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              {t("platform.onboarding.success.message") ||
                "Thank you for your interest! We've received your store request and will review it shortly."}
            </p>
            <p className="text-base text-gray-500">
              {t("platform.onboarding.success.email") ||
                "You will receive an email at"}{" "}
              <strong>{formData.adminEmail}</strong>{" "}
              {t("platform.onboarding.success.email2") ||
                "once your store is approved."}
            </p>
            <p className="text-sm text-gray-400 mt-4">
              {t("platform.onboarding.success.redirect") ||
                "Redirecting to home page..."}
            </p>
          </div>
        </div>
      </PlatformLayout>
    );
  }

  return (
    <PlatformLayout showHeader={true} headerSticky={true}>
      {/* Header Section - Editable Zone (Full Width, Zero Top) */}
      <PlatformZone pageSlug="onboarding" zoneName="header" />

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Form */}
          <div className="bg-white  p-8 md:p-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Store Name */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  {t("platform.onboarding.storeName") || "Store Name"}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    errors.name ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder={
                    t("platform.onboarding.storeNamePlaceholder") ||
                    "My Awesome Store"
                  }
                  required
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                )}
              </div>

              {/* Subdomain */}
              <div>
                <label
                  htmlFor="subdomain"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  {t("platform.onboarding.subdomain") || "Store Subdomain"}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center" dir="ltr">
                  <input
                    type="text"
                    id="subdomain"
                    name="subdomain"
                    value={formData.subdomain}
                    onChange={handleSubdomainChange}
                    className={`flex-1 px-4 py-3 border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      errors.subdomain ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder={"mystore"}
                    required
                  />
                  <span className="px-4 py-3 bg-gray-100 border border-l-0 border-gray-300 text-gray-600">
                    .{getPlatformDomain()}
                  </span>
                </div>
                {errors.subdomain && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.subdomain}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {t("platform.onboarding.subdomainHint") ||
                    "3-63 characters, letters, numbers, and hyphens only"}
                </p>
              </div>

              {/* Admin Email */}
              <div>
                <label
                  htmlFor="adminEmail"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  {t("platform.onboarding.adminEmail") || "Admin Email"}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="adminEmail"
                  name="adminEmail"
                  value={formData.adminEmail}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    errors.adminEmail ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder={"admin@mystore.com"}
                  required
                />
                {errors.adminEmail && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.adminEmail}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {t("platform.onboarding.adminEmailHint") ||
                    "You'll receive store credentials at this email after approval"}
                </p>
              </div>

              {/* Admin Name */}
              <div>
                <label
                  htmlFor="adminName"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  {t("platform.onboarding.adminName") || "Admin Name"}{" "}
                  <span className="text-gray-400">
                    ({t("platform.onboarding.optional") || "Optional"})
                  </span>
                </label>
                <input
                  type="text"
                  id="adminName"
                  name="adminName"
                  value={formData.adminName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder={
                    t("platform.onboarding.adminNamePlaceholder") || "John Doe"
                  }
                />
              </div>

              {/* Business Type */}
              <div>
                <label
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  {t("platform.onboarding.businessType") || "Business Type"}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <BusinessTypeSelector
                  value={formData.businessType}
                  onChange={(value) => {
                    setFormData((prev) => ({ ...prev, businessType: value }));
                    // Clear error when user selects a value
                    if (errors.businessType) {
                      setErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors.businessType;
                        return newErrors;
                      });
                    }
                  }}
                  error={!!errors.businessType}
                />
                {errors.businessType && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.businessType}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {t("platform.onboarding.businessTypeHint") ||
                    "What does your business sell?"}
                </p>
              </div>

              {/* Business Model Selection */}
              <div>
                <label
                  htmlFor="businessModel"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  {t("platform.onboarding.businessModel") || "Business Model"}{" "}
                  <span className="text-gray-400">
                    ({t("platform.onboarding.optional") || "Optional"})
                  </span>
                </label>
                <BusinessModelSelector
                  value={formData.businessModel}
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, businessModel: value }))
                  }
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t("platform.onboarding.businessModelHint") ||
                    "Trial includes Pay-As-You-Go. You can switch to Plugin-Based after approval."}
                </p>
              </div>

              {/* Plan Selection */}
              <div>
                <label
                  htmlFor="plan"
                  className="block text-sm font-semibold text-gray-700 mb-2"
                >
                  {t("platform.onboarding.plan") || "Select Plan"}{" "}
                  <span className="text-gray-400">
                    ({t("platform.onboarding.optional") || "Optional"})
                  </span>
                </label>
                {plansLoading ? (
                  <div className="w-full px-4 py-3 border border-gray-300 bg-gray-50 text-center text-gray-500">
                    {t("platform.onboarding.loadingPlans") ||
                      "Loading plans..."}
                  </div>
                ) : (
                  <PlanSelector
                    value={formData.plan}
                    onChange={(value) =>
                      setFormData((prev) => ({ ...prev, plan: value }))
                    }
                    options={
                      plans.length > 0
                        ? plans
                        : [
                            // Fallback if plans not loaded
                            {
                              value: "",
                              label:
                                t("platform.onboarding.planDefault") ||
                                "Free Trial (1 Month)",
                              description:
                                t("platform.onboarding.planDefaultDesc") ||
                                "Perfect for getting started",
                              price: t("platform.onboarding.free") || "Free",
                            },
                          ]
                    }
                  />
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {t("platform.onboarding.planHint") ||
                    "Trial includes Basic Plan limits. You can upgrade your plan after approval."}
                </p>
              </div>

              {/* Error Message */}
              {errors.form && (
                <div className="bg-red-50 border border-red-200 p-4">
                  <p className="text-sm text-red-600">{errors.form}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-4 font-bold text-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {t("platform.onboarding.submitting") || "Submitting..."}
                  </>
                ) : (
                  t("platform.onboarding.submit") || "Submit Store Request"
                )}
              </button>

              {/* reCAPTCHA Note */}
              <p className="text-xs text-center text-gray-500">
                {t("platform.onboarding.captchaNote") ||
                  "This form is protected by reCAPTCHA"}
              </p>
            </form>
          </div>

          {/* Info Section - Editable Zone */}
          <PlatformZone pageSlug="onboarding" zoneName="info" />
        </div>
      </div>
    </PlatformLayout>
  );
};

export default Onboarding;
